import { MiraAvailabilityState, MiraPublishedMetric, MiraPublishedPeriod } from "@/data/models/Mira";
import {
  COLOR_MIRA_OUTCOME_1,
  COLOR_MIRA_OUTCOME_1_DARK,
  COLOR_MIRA_OUTCOME_2,
  COLOR_MIRA_OUTCOME_2_DARK,
  COLOR_MIRA_OUTCOME_3,
  COLOR_MIRA_OUTCOME_3_DARK,
  COLOR_MIRA_OUTCOME_4,
  COLOR_MIRA_OUTCOME_4_DARK,
  COLOR_MIRA_OUTCOME_OTHER,
  COLOR_MIRA_OUTCOME_OTHER_DARK,
  COLOR_MIRA_SERVICE_ASSESSMENT,
  COLOR_MIRA_SERVICE_ASSESSMENT_DARK,
  COLOR_MIRA_SERVICE_DRYING,
  COLOR_MIRA_SERVICE_DRYING_DARK,
  COLOR_MIRA_SERVICE_LEAK,
  COLOR_MIRA_SERVICE_LEAK_DARK,
  COLOR_MIRA_SERVICE_REPAIR,
  COLOR_MIRA_SERVICE_REPAIR_DARK,
  COLOR_MIRA_BAR_TRACK,
  COLOR_MIRA_BAR_TRACK_DARK,
} from "@/utils/colors";
import { MiraBar, MiraSegment, MiraTile, MiraViewDelta, formatPercent, medianMeasure, metricTitle, publishedMetric, spreadLine } from "@/utils/MiraDataQualityView";
import { MiraDryingContext, MiraFact, durationFormatter, durationTileTexts, durationUnit, neutralDelta } from "@/utils/MiraDryingView";
import { MiraValueFormatter } from "@/utils/MiraTrendChart";
import { MIRA_SORTED_BAR_ROWS, buildSortedBars } from "@/utils/MiraSortedBars";
import { MiraStackedSegmentDef } from "@/utils/MiraStackedTrendView";

/**
 * The Geschäftsentwicklung screen's view model: what each tile, outcome part and bar shows, built from
 * a published period and its comparison period. Pure, like the other Mira view models.
 *
 * Decided by Taimoor on 2026-10-01, each pinned by a test:
 * - **Ablehnungsquote gets a tile only above 0.** It is 0 % for 121 of 127 clients (at most 2.4 %), so a
 *   tile would spend a headline slot on a near-constant zero; the outcome card states it instead.
 * - **Reaktionszeit counts real damage days only**, a rule IRES Cloud applies before publishing (a
 *   damage day on the start day is Office's prefilled default). Where that leaves too few, the figure is
 *   in the unlock list rather than here.
 * - As on the other screens: changes are neutral, the mean leads with the median beside it, and only a
 *   published figure gets a tile. No trend chart (Taimoor, 2026-09-30: wait for Hans).
 */

/** The outcomes as the published PRJ-003 breakdown keys them, in display order, each with its colour. */
const OUTCOMES = ["CARRY_OUT_DRYING", "NO_DRYING_NECESSARY", "ON_HOLD", "NO_CONTRACT"];
const OUTCOME_COLORS = [COLOR_MIRA_OUTCOME_1, COLOR_MIRA_OUTCOME_2, COLOR_MIRA_OUTCOME_3, COLOR_MIRA_OUTCOME_4];
const OUTCOME_COLORS_DARK = [COLOR_MIRA_OUTCOME_1_DARK, COLOR_MIRA_OUTCOME_2_DARK, COLOR_MIRA_OUTCOME_3_DARK, COLOR_MIRA_OUTCOME_4_DARK];

/**
 * The i18n key IRES Office already labels a damage-pattern outcome with ("CARRY_OUT_DRYING" to
 * `carryOutDrying`), so the ring reads exactly like the project screens.
 */
export function outcomeLabelKey(outcome: string): string {
  const camel = outcome.toLowerCase().replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase());
  return `dryingProjectOverview.damagePatternConclusionStates.${camel}`;
}

function tile(
  id: string,
  icon: string,
  value: string,
  caption: string | null,
  delta: MiraViewDelta | null,
  ctx: MiraDryingContext,
  spread: string | null = null
): MiraTile {
  return { id, icon, title: metricTitle(id, ctx), value, caption, delta, status: null, statusLabel: "", spread };
}

/** The projects with a recorded decision that were rejected, from the published share and its basis. */
function rejectedProjects(metric: MiraPublishedMetric): number {
  return Math.round((metric.value ?? 0) * (metric.basis ?? 0));
}

/**
 * The Geschäftsentwicklung KPI row: a tile for each figure the period publishes.
 *
 * A published figure whose period had nothing (`value: null`) reads "Keine Daten im Zeitraum"; one
 * waiting on the company's data is in the unlock list; Ablehnungsquote only above 0 (see the header).
 */
export function buildBusinessTiles(
  period: MiraPublishedPeriod | null,
  comparison: MiraPublishedPeriod | null,
  ctx: MiraDryingContext
): MiraTile[] {
  const tiles: MiraTile[] = [];
  const points = (magnitude: string) => ctx.t("mira.format.deltaPoints", [magnitude]);
  const plain = (magnitude: string) => magnitude;

  const add = (id: string, icon: string, build: (metric: MiraPublishedMetric, previous: MiraPublishedMetric | null) => MiraTile | null) => {
    if (period?.metrics[id]?.state !== MiraAvailabilityState.AVAILABLE) return;
    const metric = publishedMetric(period, id);
    // No tile without a value (Taimoor, 2026-10-02): what a company would unlock goes on its own page (D2).
    const built = metric ? build(metric, publishedMetric(comparison, id)) : null;
    if (built) tiles.push(built);
  };

  add("MIRA-PRJ-001", "mdi-folder-multiple-outline", (metric, previous) =>
    tile("MIRA-PRJ-001", "mdi-folder-multiple-outline", ctx.formatNumber(metric.value ?? 0),
      ctx.t("mira.businessDevelopment.captions.projects"),
      neutralDelta(metric.value, previous?.value, 0, plain, ctx), ctx));

  add("MIRA-PRJ-002", "mdi-calendar-check-outline", (metric, previous) => {
    const texts = durationTileTexts(metric, previous, ctx);
    return tile("MIRA-PRJ-002", "mdi-calendar-check-outline", texts.value,
      ctx.t("mira.businessDevelopment.captions.duration", [texts.median, ctx.formatNumber(metric.basis ?? 0)]),
      texts.delta, ctx, texts.spread);
  });

  add("MIRA-PRJ-011", "mdi-floor-plan", (metric, previous) =>
    tile("MIRA-PRJ-011", "mdi-floor-plan", ctx.formatDecimal(metric.value ?? 0, 1),
      ctx.t("mira.businessDevelopment.captions.rooms", [
        medianMeasure(metric, (value) => ctx.formatDecimal(value, 1), ctx),
        ctx.formatNumber(metric.basis ?? 0),
      ]),
      neutralDelta(metric.value, previous?.value, 1, plain, ctx), ctx,
      spreadLine(metric.p15, metric.p85, (value) => ctx.formatDecimal(value, 1), ctx)));

  add("MIRA-PRJ-008", "mdi-timer-sand", (metric, previous) => {
    const texts = durationTileTexts(metric, previous, ctx);
    return tile("MIRA-PRJ-008", "mdi-timer-sand", texts.value,
      ctx.t("mira.businessDevelopment.captions.reactionTime", [texts.median, ctx.formatNumber(metric.basis ?? 0)]),
      texts.delta, ctx, texts.spread);
  });

  // Only above 0: a near-constant zero is a fact in the outcome card, not a headline (see the header).
  add("MIRA-PRJ-004", "mdi-close-circle-outline", (metric, previous) =>
    (metric.value ?? 0) > 0
      ? tile("MIRA-PRJ-004", "mdi-close-circle-outline", formatPercent(metric.value ?? 0, ctx),
          ctx.t("mira.businessDevelopment.captions.rejections", [ctx.formatNumber(rejectedProjects(metric)), ctx.formatNumber(metric.basis ?? 0)]),
          neutralDelta((metric.value ?? 0) * 100, previous ? (previous.value ?? 0) * 100 : null, 1, points, ctx, false), ctx)
      : null);

  return tiles;
}

/**
 * The formatter the Verlauf card uses for a Geschäftsentwicklung figure, so its points read in the
 * same unit as the tile. Neutral, like every figure on the screen.
 */
export function businessTrendFormatter(metricId: string, ctx: MiraDryingContext, period: MiraPublishedPeriod | null = null): MiraValueFormatter {
  switch (metricId) {
    case "MIRA-PRJ-002":
    case "MIRA-PRJ-008":
      // In the tile's unit: hours where its headline for the chosen period is under one day.
      return durationFormatter(durationUnit(publishedMetric(period, metricId)?.value), ctx);
    case "MIRA-PRJ-011":
      return (value) => ctx.formatDecimal(value, 1);
    case "MIRA-PRJ-004":
      return (value) => formatPercent(value, ctx);
    default:
      return (value) => ctx.formatNumber(value);
  }
}

/**
 * PRJ-003 as the parts of a ring: the four outcomes seen in production in a fixed order and colour, so
 * an outcome keeps its colour between periods. Any other outcome folds into one grey "Weitere" part.
 */
export function buildOutcomeSegments(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): MiraSegment[] {
  const breakdown = publishedMetric(period, "MIRA-PRJ-003")?.breakdown ?? {};
  const total = Object.values(breakdown).reduce((sum, count) => sum + (count > 0 ? count : 0), 0);
  if (!total) return [];

  const segment = (key: string, label: string, value: number, color: string): MiraSegment => ({
    key,
    label,
    value,
    valueText: ctx.formatNumber(value),
    shareText: formatPercent(value / total, ctx),
    color,
  });

  const known = OUTCOMES.map((outcome, index) => ({ outcome, index, value: breakdown[outcome] ?? 0 }))
    .filter((part) => part.value > 0)
    .map((part) => segment(part.outcome, ctx.t(outcomeLabelKey(part.outcome)), part.value,
      ctx.dark ? OUTCOME_COLORS_DARK[part.index] : OUTCOME_COLORS[part.index]));

  const others = Object.entries(breakdown).filter(([outcome, count]) => !OUTCOMES.includes(outcome) && count > 0);
  const othersTotal = others.reduce((sum, [, count]) => sum + count, 0);
  if (othersTotal) {
    known.push(segment("OTHER", ctx.t("mira.format.others", [ctx.formatNumber(others.length)]), othersTotal,
      ctx.dark ? COLOR_MIRA_OUTCOME_OTHER_DARK : COLOR_MIRA_OUTCOME_OTHER));
  }

  return known;
}

/** The ring's centre: how many projects recorded an outcome. */
export function outcomeTotalText(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): string {
  const metric = publishedMetric(period, "MIRA-PRJ-003");
  return metric ? ctx.formatNumber(metric.value ?? 0) : "";
}

/**
 * Ablehnungsquote as a fact under the ring: "keine im Zeitraum" at 0, otherwise how many of the
 * projects with a recorded decision were rejected. Empty when the figure is not published.
 */
export function rejectionFacts(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): MiraFact[] {
  const metric = period?.metrics["MIRA-PRJ-004"]?.state === MiraAvailabilityState.AVAILABLE ? publishedMetric(period, "MIRA-PRJ-004") : null;
  if (!metric) return [];

  return [{
    label: ctx.t("mira.businessDevelopment.facts.rejections"),
    value: (metric.value ?? 0) > 0
      ? ctx.t("mira.businessDevelopment.facts.rejectedOf", [
          ctx.formatNumber(rejectedProjects(metric)),
          ctx.formatNumber(metric.basis ?? 0),
          formatPercent(metric.value ?? 0, ctx),
        ])
      : ctx.t("mira.businessDevelopment.facts.noRejections"),
  }];
}

/** PRJ-006 as sorted bars: projects per branch, named by the caller from the client's branches. */
export function buildBranchRows(period: MiraPublishedPeriod | null, names: Record<string, string>, ctx: MiraDryingContext): MiraBar[] {
  return buildSortedBars(publishedMetric(period, "MIRA-PRJ-006")?.breakdown, names, ctx.t("mira.businessDevelopment.unknownBranch"), ctx, "share", "projects");
}

/**
 * PRJ-007 as sorted bars: projects per project type. Hidden for a client with a single type, the
 * median case: one bar at 100 % says nothing the tile does not.
 */
export function buildProjectTypeRows(period: MiraPublishedPeriod | null, names: Record<string, string>, ctx: MiraDryingContext): MiraBar[] {
  const breakdown = publishedMetric(period, "MIRA-PRJ-007")?.breakdown ?? {};
  if (Object.values(breakdown).filter((count) => count > 0).length < 2) return [];

  return buildSortedBars(breakdown, names, ctx.t("mira.businessDevelopment.unknownProjectType"), ctx, "share", "projects");
}

// --- Schadenursachen (MIRA-PRJ-013): the causes the damage assessments recorded, Track #2610040 R9 ---

/** PRJ-013's reserved breakdown keys beside the cause names (IRES Cloud `metrics/damageCause.ts`). */
const DAMAGE_CAUSE_FINISHED_KEY = "__finished";
const DAMAGE_CAUSE_UNDEFINED_KEY = "__undefined";

/**
 * PRJ-013 as sorted bars: the finished IRES Project projects per Schadenursache, largest first, each bar
 * its share of the projects with a cause and its project count beside (#2610112 X2). Every selected cause
 * counts (Hans, 2026-10-14), so a project with two causes is in two bars: the shares are of the projects
 * with a cause (PRJ-013's value), never of the bars' sum; the folded "Weitere" row too, its share the sum of the causes it folds. The cause names are the Beschreibung's own
 * wording, carried as the breakdown keys; an option the project's Beschreibung version does not define
 * folds into one "unbekannte Ursache" bar.
 */
export function buildDamageCauseRows(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): MiraBar[] {
  const breakdown = { ...(publishedMetric(period, "MIRA-PRJ-013")?.breakdown ?? {}) };
  delete breakdown[DAMAGE_CAUSE_FINISHED_KEY];
  const names = Object.fromEntries(
    Object.keys(breakdown).filter((key) => key !== DAMAGE_CAUSE_UNDEFINED_KEY).map((key) => [key, key])
  );
  // Each cause's share of the projects with a cause, percentage first (#2610112 X2); a project with two
  // causes counts under both, so the shares add up to more than 100 %, as the subtitle says.
  const withCause = publishedMetric(period, "MIRA-PRJ-013")?.value ?? null;
  return buildSortedBars(breakdown, names, ctx.t("mira.businessDevelopment.unknownDamageCause"), ctx, "count", "projects", withCause);
}

/** One cause in the Markt view of Schadenursachen: the company's own share, then the Markt's. */
export interface MiraDamageCauseMarketRow {
  key: string;
  label: string;
  bars: MiraBar[];
}

/** Schadenursachen in Markt mode: the causes, the causes left out, and the Markt entities with no figure. */
export interface MiraDamageCauseMarket {
  rows: MiraDamageCauseMarketRow[];
  /** Causes beyond the shown rows. */
  restCount: number;
  /** The labels of the Markt entities that withhold the figure (too few companies). */
  withoutValue: string[];
  /**
   * The card's one legend (Hans, #2610112 X3): each entity with a bar, in bar order, so the bars under a
   * cause carry no label of their own, as in the timeline cards.
   */
  legend: Array<{ key: string; label: string; color: string }>;
}

/**
 * Schadenursachen against the Markt (Taimoor, 2026-10-27): per cause, its **share of the projects with a
 * cause** for the company and Markt je Projekt (Markt je Mandant dropped, #2610112 X6a), in that fixed order
 * and on one scale, the bars directly below each other under one legend (X3). IRES
 * Cloud carries the Markt's PRJ-013 breakdown as shares already (`marketCauseShares`); the company's own
 * counts become shares here. A project with two causes counts under both, so a column adds up to more than
 * 100 %. The causes are ranked by the company's own share, then the Markt je Projekt share; the first
 * {@link MIRA_SORTED_BAR_ROWS} are shown. A company with no cause recorded gets no rows (the card is hidden).
 *
 * @param entities - the company first, then the Markt, each with its period and colour.
 */
export function buildDamageCauseMarket(
  entities: Array<{ id: string; label: string; period: MiraPublishedPeriod | null; isMarket: boolean; color: string }>,
  ctx: MiraDryingContext
): MiraDamageCauseMarket {
  const withShares: Array<{ id: string; label: string; color: string; isMarket: boolean; shares: Record<string, number> }> = [];
  const withoutValue: string[] = [];
  for (const entity of entities) {
    const metric = publishedMetric(entity.period, "MIRA-PRJ-013");
    const breakdown = metric?.breakdown;
    const basis = metric?.basis ?? 0;
    if (!breakdown || !(basis > 0)) {
      if (entity.isMarket) withoutValue.push(entity.label);
      continue;
    }
    const shares: Record<string, number> = {};
    Object.entries(breakdown)
      .filter(([key]) => key !== DAMAGE_CAUSE_FINISHED_KEY)
      .forEach(([key, value]) => {
        shares[key] = entity.isMarket ? value : value / basis;
      });
    withShares.push({ id: entity.id, label: entity.label, color: entity.color, isMarket: entity.isMarket, shares });
  }

  const own = withShares.find((entity) => !entity.isMarket);
  if (!own) {
    return { rows: [], restCount: 0, withoutValue, legend: [] };
  }
  // Ties in the company's own share break on the last Markt entity shown (je Projekt where it has a value).
  const tieBreak = [...withShares].reverse().find((entity) => entity.isMarket) ?? own;
  const causes = [...new Set(withShares.flatMap((entity) => Object.keys(entity.shares)))].sort(
    (a, b) =>
      (own.shares[b] ?? 0) - (own.shares[a] ?? 0) ||
      (tieBreak.shares[b] ?? 0) - (tieBreak.shares[a] ?? 0) ||
      a.localeCompare(b)
  );
  const shown = causes.slice(0, MIRA_SORTED_BAR_ROWS);
  const largest = Math.max(0, ...shown.flatMap((cause) => withShares.map((entity) => entity.shares[cause] ?? 0)));
  const trackColor = ctx.dark ? COLOR_MIRA_BAR_TRACK_DARK : COLOR_MIRA_BAR_TRACK;

  return {
    rows: shown.map((cause) => ({
      key: cause,
      label: cause === DAMAGE_CAUSE_UNDEFINED_KEY ? ctx.t("mira.businessDevelopment.unknownDamageCause") : cause,
      bars: withShares.map((entity) => {
        const share = entity.shares[cause] ?? 0;
        return {
          key: `${cause}-${entity.id}`,
          label: entity.label,
          fraction: largest > 0 ? share / largest : 0,
          valueText: formatPercent(share, ctx),
          countText: null,
          color: entity.color,
          trackColor,
          deltaText: null,
          deltaDirection: "flat" as const,
          showDelta: false,
        };
      }),
    })),
    restCount: causes.length - shown.length,
    withoutValue,
    legend: withShares.map((entity) => ({ key: entity.id, label: entity.label, color: entity.color })),
  };
}

/**
 * The card's subtitle: how many of the finished projects record a cause at all ("412 von 1.250
 * abgeschlossenen Projekten mit Schadenursache"), since most do not.
 */
export function damageCauseCoverage(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): string {
  const metric = publishedMetric(period, "MIRA-PRJ-013");
  const finished = metric?.breakdown?.[DAMAGE_CAUSE_FINISHED_KEY] ?? 0;
  return ctx.t("mira.businessDevelopment.cards.damageCausesSubtitle", [
    ctx.formatNumber(metric?.value ?? 0),
    ctx.formatNumber(finished),
  ]);
}

/** The card's subtitle in Markt mode: shares of the company's projects with a cause, and why they exceed 100 %. */
export function damageCauseMarketCoverage(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): string {
  const metric = publishedMetric(period, "MIRA-PRJ-013");
  return ctx.t("mira.businessDevelopment.cards.damageCausesMarketSubtitle", [ctx.formatNumber(metric?.value ?? 0)]);
}

// --- Leistungen (MIRA-PRJ-017): the four services performed, stacked over time (2610009) ---

/** The services PRJ-017 counts, in display order, each with its badge hue. The keys match the segments. */
const SERVICES = [
  { key: "leakDetection", color: COLOR_MIRA_SERVICE_LEAK, dark: COLOR_MIRA_SERVICE_LEAK_DARK },
  { key: "damageAssessment", color: COLOR_MIRA_SERVICE_ASSESSMENT, dark: COLOR_MIRA_SERVICE_ASSESSMENT_DARK },
  { key: "drying", color: COLOR_MIRA_SERVICE_DRYING, dark: COLOR_MIRA_SERVICE_DRYING_DARK },
  { key: "repair", color: COLOR_MIRA_SERVICE_REPAIR, dark: COLOR_MIRA_SERVICE_REPAIR_DARK },
];

/**
 * The four services as stack parts, in the project list badge hues (Leckortung orange, Schadenaufnahme
 * brown, Trocknung blue, Wiederherstellung dark blue). Each part is a count of projects that finished
 * that service in the period; a project counts in every service it finished, so the parts do not sum to
 * the distinct project count (the caption says so).
 *
 * A service with no project anywhere in the drawn timeline is left out of the chart and legend
 * (`hideWhenEmpty`, Taimoor, 2026-10-07): GZ projects never record Leckortung, Schadenaufnahme or
 * Wiederherstellung (measured across all clients that day), so a GZ-only client such as BELFOR or svt
 * would otherwise see three services it does not offer, at zero. A service the chosen period counts is
 * always kept, so its legend value is never dropped even where that period lies outside the timeline.
 */
export function businessStackSegments(ctx: MiraDryingContext, period: MiraPublishedPeriod | null = null): MiraStackedSegmentDef[] {
  const breakdown = publishedMetric(period, "MIRA-PRJ-017")?.breakdown ?? {};
  return SERVICES.map((service) => ({
    key: service.key,
    label: ctx.t(`mira.businessDevelopment.services.${service.key}`),
    color: ctx.dark ? service.dark : service.color,
    hideWhenEmpty: !((breakdown[service.key] ?? 0) > 0),
  }));
}

/** Whether the Leistungen card is drawn: MIRA-PRJ-017 is AVAILABLE for the chosen period. */
export function businessStackAvailable(period: MiraPublishedPeriod | null): boolean {
  return period?.metrics["MIRA-PRJ-017"]?.state === MiraAvailabilityState.AVAILABLE;
}

/** The stack parts are project counts, so the bar total reads as a plain number. */
export function businessStackFormatter(ctx: MiraDryingContext): (value: number) => string {
  return (value) => ctx.formatNumber(value);
}

/** The fixed caption: a project counts in every service it finished, so the parts can exceed the projects. */
export function businessStackCaption(ctx: MiraDryingContext): string {
  return ctx.t("mira.businessDevelopment.services.caption");
}

/**
 * The chosen period's count per service, keyed as the segments are, so the legend can read "Trocknung
 * (812)". Empty where the figure is absent.
 */
export function businessStackLegendValues(period: MiraPublishedPeriod | null, ctx: MiraDryingContext): Record<string, string> {
  const breakdown = publishedMetric(period, "MIRA-PRJ-017")?.breakdown ?? {};
  const values: Record<string, string> = {};
  SERVICES.forEach((service) => {
    values[service.key] = ctx.formatNumber(breakdown[service.key] ?? 0);
  });
  return values;
}
